import axios from 'axios';
import { getBaseUrl } from 'utils/url';
import {
  Notification,
  NotificationResponse,
  UnreadCountResponse,
  MarkReadResponse,
  NotificationQuery,
} from 'types/notifications';

class NotificationService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getBaseUrl();
  }

  private getHeaders(token: string) {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  async getAllNotifications(
    token: string,
    query?: NotificationQuery,
  ): Promise<Notification[]> {
    try {
      const params = new URLSearchParams();
      if (query?.limit) {
        params.append('limit', query.limit.toString());
      }
      if (query?.unread_only) {
        params.append('unread_only', query.unread_only.toString());
      }

      const url = `${this.baseUrl}/api/notifications${
        params.toString() ? `?${params.toString()}` : ''
      }`;

      const response = await axios.get<NotificationResponse>(url, {
        headers: this.getHeaders(token),
      });

      return response.data.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  async getUnreadCount(token: string): Promise<number> {
    try {
      const response = await axios.get<UnreadCountResponse>(
        `${this.baseUrl}/api/notifications/unread-count`,
        {
          headers: this.getHeaders(token),
        },
      );

      return response.data.data.count;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  }

  async markAsRead(token: string, notificationId: string): Promise<void> {
    try {
      await axios.patch<MarkReadResponse>(
        `${this.baseUrl}/api/notifications/${notificationId}/read`,
        {},
        {
          headers: this.getHeaders(token),
        },
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  async markAllAsRead(token: string): Promise<void> {
    try {
      await axios.patch<MarkReadResponse>(
        `${this.baseUrl}/api/notifications/mark-all-read`,
        {},
        {
          headers: this.getHeaders(token),
        },
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }
}

export const notificationService = new NotificationService();
